import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Radio,
  Switch,
  Button,
  Upload,
  Space,
  Image,
  InputNumber,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";

import { getGroupCategories } from "~/services/groups";

const { TextArea } = Input;
const { Option } = Select;

export const GROUP_KINDS = [
  { value: "wechat", label: "微信" },
  { value: "telegram", label: "Telegram" },
  { value: "slack", label: "Slack" },
  { value: "QQ", label: "QQ" },
  { value: "discord", label: "Discord" },
];

/**
 * 群组申请表单组件
 *
 * @param {Object} props
 * @param {Object} props.form - Ant Design Form 实例（可选，如果不提供会内部创建）
 * @param {Function} props.onSubmit - 提交回调函数，接收表单值作为参数
 * @param {Object} props.initialValues - 初始表单值（用于编辑模式）
 * @param {boolean} props.loading - 是否正在提交
 * @param {boolean} props.showSubmitButton - 是否显示提交按钮（默认true）
 * @param {boolean} props.isEditMode - 是否为编辑模式（默认false，编辑模式下不强制要求群人数>=50）
 */
const GroupApplicationForm = ({
  form: externalForm,
  onSubmit,
  initialValues,
  loading = false,
  showSubmitButton = true,
  isEditMode = false,
}) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;

  const [categories, setCategories] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [qrcodeFile, setQrcodeFile] = useState(null);
  const [qrcodePreview, setQrcodePreview] = useState(null);

  // 监听表单的 kind 字段变化
  const groupKind = Form.useWatch("kind", form) || "wechat";

  useEffect(() => {
    getGroupCategories()
      .then((categories) => {
        setCategories(categories);
        if (categories.length > 0 && !form.getFieldValue("group_category_id")) {
          form.setFieldsValue({ group_category_id: categories[0].id });
        }
      })
      .catch((err) => {
        console.error("Failed to load categories:", err);
        message.error("加载群组分类失败");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 如果有初始值，设置预览图片
  useEffect(() => {
    if (initialValues) {
      if (initialValues.logo) {
        setLogoPreview(initialValues.logo);
      }
      if (initialValues.qrcode) {
        setQrcodePreview(initialValues.qrcode);
      }
    }
  }, [initialValues]);

  const handleLogoChange = (info) => {
    if (info.file.status === "removed") {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }

    const file = info.file.originFileObj || info.file;
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQrcodeChange = (info) => {
    if (info.file.status === "removed") {
      setQrcodeFile(null);
      setQrcodePreview(null);
      form.setFieldsValue({ qrcode: undefined });
      return;
    }

    const file = info.file.originFileObj || info.file;
    if (file) {
      setQrcodeFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setQrcodePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKindChange = (e) => {
    const kind = e.target.value;
    // 切换类型时清空相关字段
    if (kind === "wechat" || kind === "QQ") {
      form.setFieldsValue({ link: undefined });
    } else {
      form.setFieldsValue({ qrcode: undefined });
      setQrcodeFile(null);
      setQrcodePreview(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 验证群人数（新建时要求至少50人，编辑时不做限制）
      if (!isEditMode && values.member_count < 50) {
        message.error("群人数不少于50人才能申请");
        return;
      }

      // 验证入群方式
      const isQrcodeType = values.kind === "wechat" || values.kind === "QQ";
      if (isQrcodeType && !qrcodeFile && !qrcodePreview) {
        message.error("请上传进群二维码");
        return;
      }
      if (!isQrcodeType && !values.link) {
        message.error("请填写加群链接");
        return;
      }

      // 准备表单数据
      const formData = {
        name: values.name.trim(),
        description: values.description.trim(),
        member_count: values.member_count,
        ele: values.ele,
        paid: values.paid || false,
        kind: values.kind,
        group_category_id: values.group_category_id,
        logoFile,
        qrcodeFile: isQrcodeType ? qrcodeFile : null,
        link: !isQrcodeType ? values.link?.trim() : null,
      };

      // 调用外部提交处理函数
      if (onSubmit) {
        await onSubmit(formData);
      }
    } catch (err) {
      // 表单验证错误会被 Form 自动处理
      if (err.errorFields) {
        return;
      }
      message.error(err.message || "表单验证失败");
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      initialValues={{
        kind: "wechat",
        paid: false,
        member_count: 50,
        ...initialValues,
      }}
    >
      <Form.Item
        label="群名称"
        name="name"
        rules={[
          { required: true, message: "请输入群名称" },
          { max: 10, message: "群名称最多10个字" },
          { transform: (value) => value?.trim() },
        ]}
      >
        <Input
          placeholder="请输入群名称（最多10个字）"
          maxLength={10}
        />
      </Form.Item>

      <Form.Item
        label="群简介/群公告"
        name="description"
        extra="建群目的，群内容，入群须知等"
        rules={[
          { required: true, message: "请输入群简介/群公告" },
          { transform: (value) => value?.trim() },
        ]}
      >
        <TextArea
          rows={5}
          placeholder="请输入建群目的，群内容，入群须知等"
          showCount
        />
      </Form.Item>

      <Form.Item
        label="目前群人数"
        name="member_count"
        extra={
          isEditMode
            ? "群人数需入群验证审核"
            : "群人数不少于50人才能申请且需入群验证审核"
        }
        rules={[
          { required: true, message: "请输入群人数" },
          {
            type: "number",
            min: isEditMode ? 0 : 50,
            message: isEditMode ? "群人数必须大于等于0" : "群人数不少于50人",
          },
        ]}
      >
        <InputNumber
          min={isEditMode ? 0 : 50}
          style={{ width: "100%" }}
          placeholder="请输入群人数"
        />
      </Form.Item>

      <Form.Item
        label="入群电量"
        name="ele"
        rules={[
          { required: true, message: "请输入入群消耗电量" },
          { type: "number", min: 0, message: "入群消耗电量必须大于等于0" },
        ]}
      >
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          placeholder="请输入入群消耗电量"
        />
      </Form.Item>

      <Form.Item
        label="是否付费"
        name="paid"
        valuePropName="checked"
      >
        <Switch
          checkedChildren="付费"
          unCheckedChildren="免费"
        />
      </Form.Item>

      <Form.Item
        label="群类型"
        name="kind"
        rules={[{ required: true, message: "请选择群类型" }]}
      >
        <Radio.Group onChange={handleKindChange}>
          {GROUP_KINDS.map((kind) => (
            <Radio
              key={kind.value}
              value={kind.value}
            >
              {kind.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      {groupKind === "wechat" || groupKind === "QQ" ? (
        <Form.Item
          label="进群二维码"
          name="qrcode"
          rules={[{ required: true, message: "请上传进群二维码" }]}
        >
          <Space
            direction="vertical"
            style={{ width: "100%" }}
          >
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleQrcodeChange}
            >
              <Button icon={<UploadOutlined />}>选择二维码图片</Button>
            </Upload>
            {qrcodePreview && (
              <Image
                src={qrcodePreview}
                alt="qrcode preview"
                width={200}
                height={200}
              />
            )}
          </Space>
        </Form.Item>
      ) : (
        <Form.Item
          label="加群链接"
          name="link"
          rules={[
            { required: true, message: "请填写加群链接" },
            { type: "url", message: "请输入有效的链接地址" },
            { transform: (value) => value?.trim() },
          ]}
        >
          <Input placeholder="请输入加群链接" />
        </Form.Item>
      )}

      <Form.Item
        label="群组分类"
        name="group_category_id"
        rules={[{ required: true, message: "请选择群组分类" }]}
      >
        <Select placeholder="请选择群组分类">
          {categories.map((category) => (
            <Option
              key={category.id}
              value={category.id}
            >
              {category.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="群 Logo"
        name="logo"
        extra="可以是任何跟群主题相关的图标"
        rules={[{ required: true, message: "请上传群 Logo" }]}
      >
        <Space
          direction="vertical"
          style={{ width: "100%" }}
        >
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleLogoChange}
          >
            <Button icon={<UploadOutlined />}>选择图片</Button>
          </Upload>
          {logoPreview && (
            <Image
              src={logoPreview}
              alt="logo preview"
              width={120}
              height={120}
            />
          )}
        </Space>
      </Form.Item>

      {showSubmitButton && (
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            disabled={loading}
            loading={loading}
          >
            {loading ? "提交中..." : "提交"}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default GroupApplicationForm;
